import { request } from "$/lib/api"
import {
  JobLabel,
  Route,
  SaveJobLabel,
  SetJobLabels,
  UpdateJobLabel,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface JobLabelFilters {
  storeId: string
}

async function fetchJobLabels(filters: JobLabelFilters): Promise<JobLabel[]> {
  return await request<JobLabel[]>({
    url: populateRoute(Route.JOB_LABEL__FIND_ALL, filters.storeId),
    method: "GET",
  })
}

async function saveJobLabel(
  data: SaveJobLabel,
  storeId: string,
): Promise<JobLabel> {
  return await request<JobLabel>({
    url: populateRoute(Route.JOB_LABEL__SAVE, storeId),
    method: "POST",
    data,
  })
}

async function updateJobLabel(data: UpdateJobLabel, jobLabelId: string) {
  return await request<JobLabel>({
    url: populateRoute(Route.JOB_LABEL__UPDATE, jobLabelId),
    method: "PATCH",
    data,
  })
}

async function deleteJobLabel(jobLabelId: string) {
  return await request<void>({
    url: populateRoute(Route.JOB_LABEL__DELETE, jobLabelId),
    method: "DELETE",
  })
}

async function setJobLabels(jobId: string, data: SetJobLabels) {
  return await request<void>({
    url: populateRoute(Route.JOB_LABEL__SET_LINKS_FOR_JOB, jobId),
    method: "PUT",
    data,
  })
}

export function useSetJobLabelsMutation(jobId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (labelIds: string[]) => setJobLabels(jobId, { labelIds }),
    // Each call replaces the job's labels, so quick toggles must land in order.
    scope: { id: `set-job-labels-${jobId}` },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["jobs"] }),
        queryClient.invalidateQueries({ queryKey: ["calendar"] }),
      ])
    },
  })
}

export function useJobLabelMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: jobLabelQueries.lists() }),
      queryClient.invalidateQueries({ queryKey: ["job"] }),
      queryClient.invalidateQueries({ queryKey: ["calendar"] }),
    ])
  }

  const saveMutation = useMutation({
    mutationFn: (data: SaveJobLabel) => saveJobLabel(data, storeId),
    onSettled: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ data, id }: { data: UpdateJobLabel; id: string }) =>
      updateJobLabel(data, id),
    onSettled: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteJobLabel(id),
    onSettled: invalidate,
  })

  return { saveMutation, updateMutation, deleteMutation }
}

const jobLabelQueries = {
  all: () => ["job-label"],
  lists: () => [...jobLabelQueries.all(), "list"],
  list: (filters: JobLabelFilters) =>
    queryOptions({
      queryKey: [...jobLabelQueries.lists(), filters],
      queryFn: () => fetchJobLabels(filters),
    }),
  customList: (filters: JobLabelFilters) =>
    queryOptions({
      queryKey: [...jobLabelQueries.lists(), "custom", filters],
      queryFn: async () => {
        const labels = await fetchJobLabels(filters)
        return labels.filter((label) => !label.isSystem)
      },
    }),
}

export default jobLabelQueries
